import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ActivityTable = ({ logs, sortConfig, onSort, currentPage, totalPages, onPageChange, totalLogs }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const getSeverityColor = (severity) => {
    const colors = {
      Info: 'bg-blue-100 text-blue-800 border-blue-200',
      Warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Error: 'bg-red-100 text-red-800 border-red-200',
      Critical: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors?.[severity] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusColor = (status) => {
    return status === 'Success' ?'bg-success/10 text-success border-success/20' :'bg-destructive/10 text-destructive border-destructive/20';
  };

  const getModuleIcon = (module) => {
    const icons = {
      BMC: 'Grid',
      Predictive: 'TrendingUp',
      Goal: 'Target',
      Authentication: 'Lock',
      Payment: 'CreditCard',
      Subscription: 'Package',
      Reports: 'FileText',
      Administrative: 'Shield',
      API: 'Code'
    };
    return icons?.[module] || 'Activity';
  };

  const handleRowClick = (logId) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={14} className="text-primary" />
      : <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  return (
    <div className="bg-card border border-border rounded-lg elevation-sm overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('timestamp')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground font-heading hover:text-primary transition-smooth"
                >
                  Timestamp
                  <SortIcon columnKey="timestamp" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('clientName')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground font-heading hover:text-primary transition-smooth"
                >
                  Client Name
                  <SortIcon columnKey="clientName" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('actionPerformed')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground font-heading hover:text-primary transition-smooth"
                >
                  Action
                  <SortIcon columnKey="actionPerformed" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-foreground font-heading">Module</span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-foreground font-heading">IP Address</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground font-heading hover:text-primary transition-smooth"
                >
                  Status
                  <SortIcon columnKey="status" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('severity')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground font-heading hover:text-primary transition-smooth"
                >
                  Severity
                  <SortIcon columnKey="severity" />
                </button>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-xs font-semibold text-foreground font-heading">Details</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {logs?.map((log) => (
              <React.Fragment key={log?.id}>
                <tr className="hover:bg-muted/50 transition-smooth">
                  <td className="px-4 py-3">
                    <div className="text-sm text-foreground font-body">
                      {log?.timestamp?.split(' ')?.[0]}
                    </div>
                    <div className="text-xs text-muted-foreground font-caption">
                      {log?.timestamp?.split(' ')?.[1]}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-foreground font-body">
                      {log?.clientName}
                    </div>
                    <div className="text-xs text-muted-foreground font-caption">
                      {log?.userName}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground font-body">
                      {log?.actionPerformed}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                        <Icon name={getModuleIcon(log?.moduleUsed)} size={16} color="var(--color-primary)" />
                      </div>
                      <span className="text-sm text-foreground font-body">{log?.moduleUsed}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground font-mono">
                      {log?.ipAddress}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(log?.status)}`}>
                      <Icon 
                        name={log?.status === 'Success' ? 'CheckCircle' : 'XCircle'} 
                        size={12} 
                      />
                      {log?.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(log?.severity)}`}>
                      {log?.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleRowClick(log?.id)}
                      className="p-1 hover:bg-muted rounded transition-smooth"
                      aria-label="Toggle details"
                    >
                      <Icon 
                        name={expandedRow === log?.id ? 'ChevronUp' : 'ChevronDown'} 
                        size={16} 
                        className="text-muted-foreground"
                      />
                    </button>
                  </td>
                </tr>
                {expandedRow === log?.id && (
                  <tr>
                    <td colSpan="8" className="px-4 py-3 bg-muted/30">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <Icon name="Info" size={16} className="text-primary mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold text-foreground font-heading">Details:</p>
                            <p className="text-sm text-muted-foreground font-body mt-1">{log?.details}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground font-caption">
                          <span>User Type: <span className="font-medium text-foreground">{log?.userType}</span></span>
                          <span>Log ID: <span className="font-medium text-foreground">#{log?.id}</span></span>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {logs?.map((log) => (
          <div key={log?.id} className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                    <Icon name={getModuleIcon(log?.moduleUsed)} size={16} color="var(--color-primary)" />
                  </div>
                  <span className="text-sm font-medium text-foreground font-body">{log?.moduleUsed}</span>
                </div>
                <h3 className="text-sm font-semibold text-foreground font-heading">
                  {log?.actionPerformed}
                </h3>
                <p className="text-xs text-muted-foreground font-caption mt-1">
                  {log?.clientName} • {log?.userName}
                </p>
              </div>
              <button
                onClick={() => handleRowClick(log?.id)}
                className="p-1 hover:bg-muted rounded transition-smooth"
                aria-label="Toggle details"
              >
                <Icon 
                  name={expandedRow === log?.id ? 'ChevronUp' : 'ChevronDown'} 
                  size={16} 
                  className="text-muted-foreground"
                />
              </button>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(log?.status)}`}>
                <Icon 
                  name={log?.status === 'Success' ? 'CheckCircle' : 'XCircle'} 
                  size={12} 
                />
                {log?.status}
              </span>
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(log?.severity)}`}>
                {log?.severity}
              </span>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground font-caption">
              <span>{log?.timestamp}</span>
              <span className="font-mono">{log?.ipAddress}</span>
            </div>

            {expandedRow === log?.id && (
              <div className="pt-3 border-t border-border space-y-2">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={16} className="text-primary mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-foreground font-heading">Details:</p>
                    <p className="text-sm text-muted-foreground font-body mt-1">{log?.details}</p>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground font-caption">
                  User Type: <span className="font-medium text-foreground">{log?.userType}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border bg-muted/30">
        <p className="text-sm text-muted-foreground font-caption">
          Showing <span className="font-medium text-foreground">{((currentPage - 1) * 10) + 1}</span> to{' '}
          <span className="font-medium text-foreground">{Math.min(currentPage * 10, totalLogs)}</span> of{' '}
          <span className="font-medium text-foreground">{totalLogs}</span> logs
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-md border border-border bg-card hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
            aria-label="Previous page"
          >
            <Icon name="ChevronLeft" size={16} />
          </button>
          <span className="text-sm font-medium text-foreground font-body px-3">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-md border border-border bg-card hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
            aria-label="Next page"
          >
            <Icon name="ChevronRight" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityTable;